import { Button, IconButton, Link, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'react-use'

import { useIsSubscriptionEligibleForEInvoicing } from '@features/invoices/hooks/eInvoices/useIsSubscriptionEligibleForEInvoicing'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EInvoicesDashboardBanner } from '../../../../assets/images'
import { LocalStorageKeys } from '../../../../enums/localStorageKeys'
import { useSegment } from '../../../../hooks'
import { getImageClassName } from '../../../../utils/getClassName'
import { E_INVOICING_SUPPORT_LINK, KYC_REGISTRATION_COMPLETED_STATUS } from '../../../eInvoicing/constants'
import { useEInvoicingSetup } from '../../../eInvoicing/hooks/useEInvoicingSetup'
import { getEInvoicingBaseUrl } from '../../../eInvoicing/utils'
import * as Styled from './styles'

type EInvoicingDashboardBannerSource =
  | 'dashboard'
  | 'bills-list'
  | 'receipts-list'
  | 'settings'
  | 'unknown'
  | 'bills-list-empty'
  | 'invoice-editor'

interface EInvoicingDashboardBannerProps {
  source?: EInvoicingDashboardBannerSource
}

export const EInvoicingDashboardBanner = ({ source }: EInvoicingDashboardBannerProps = {}): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const [isBannerClosed, setIsBannerClosed] = useLocalStorage<boolean>(
    LocalStorageKeys.DashboardEInvoicingBanner,
    false,
  )
  const { organization } = useUserOrganization()
  const { kycStatus, isEInvoicingEnabled, eInvoiceIntegration } = useEInvoicingSetup()
  const { isSubscriptionEligibleForEInvoicing } = useIsSubscriptionEligibleForEInvoicing()

  const shouldHideBanner =
    isBannerClosed ||
    KYC_REGISTRATION_COMPLETED_STATUS.includes(kycStatus) ||
    !isEInvoicingEnabled ||
    eInvoiceIntegration ||
    !isSubscriptionEligibleForEInvoicing

  const handleCloseButtonClick = useCallback(() => {
    setIsBannerClosed(true)
    track('xxx - e-invoicing: Closed "Activate for Free" (Client)', {
      eInvoicingBannerSource: (source ?? 'unknown') as EInvoicingDashboardBannerSource,
    })
  }, [setIsBannerClosed, track, source])

  const handleCTAClick = useCallback(() => {
    track('xxx - e-invoicing: Clicked "Activate for Free" (Client)')
  }, [track])

  useEffect(() => {
    if (shouldHideBanner) {
      return
    }
    // NOTE: tracking this allows us to compare users that saw
    // the info "at least onece" vs users that clicked it,
    // independently on the location of the banner
    track('xxx - e-invoicing: Viewed "Activate for Free" (Client)', {
      // Using this name to not overwrite any other common name
      eInvoicingBannerSource: (source ?? 'unknown') as EInvoicingDashboardBannerSource,
    })
  }, [shouldHideBanner, track, source])

  if (shouldHideBanner) {
    return null
  }

  return (
    <>
      <Styled.EInvoicingBannerWrapper>
        <Styled.IllustrationContainer className={getImageClassName()}>
          <EInvoicesDashboardBanner />
        </Styled.IllustrationContainer>
        <Styled.ContentWrapper>
          <Styled.TextWrapper>
            <Text variant="h4" weight="bold">
              {t('dashboard.banner.e_invoicing.title')}
            </Text>
            <Text>
              {t('dashboard.banner.e_invoicing.subtitle')}{' '}
              <Link href={E_INVOICING_SUPPORT_LINK} target="_blank" rel="noopener noreferrer">
                {t('dashboard.banner.e_invoicing.link')}
              </Link>
            </Text>
          </Styled.TextWrapper>
          <Styled.CtaWrapper>
            <Button
              to={getEInvoicingBaseUrl(organization)}
              onClick={handleCTAClick}
              size="sm"
              variant="onColor"
              color="success"
            >
              {t('dashboard.banner.e_invoicing.cta')}
            </Button>
          </Styled.CtaWrapper>
        </Styled.ContentWrapper>
        <Styled.CloseIconWrapper>
          <IconButton icon="x" onClick={handleCloseButtonClick} size="sm" variant="text" />
        </Styled.CloseIconWrapper>
      </Styled.EInvoicingBannerWrapper>
    </>
  )
}
